/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ButtonGroup, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';

import GroupSelectList from 'bundles/enterprise-admin-learners/components/GroupSelectList';
import { useContractEligibleGroupIds } from 'bundles/enterprise-admin-learners/hooks/useContractEligibleGroupIds';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
};

type GroupAssignmentStepBaseProps = {
  open: boolean;
  onBack: () => void;
  onSubmit: (groupId: string) => void | Promise<void>;
  /** Caller's mutation/request loading state. */
  submitting: boolean;
  /** Caller's submission error, if any. */
  errorMessage?: string;
  /** Set for multi-contract orgs — hides groups with no roster under this contract from the picker. */
  selectedContractId?: string;
};

/**
 * `onSkip` is required whenever `skippable` is true — both the dialog's close control and
 * the alternate action button call it unconditionally in that mode, so an omitted `onSkip`
 * would otherwise only surface as a runtime TypeError on click.
 */
export type GroupAssignmentStepProps = GroupAssignmentStepBaseProps &
  (
    | {
        /** True for org admins with no group already known — Phase A always passes false/omitted. */
        skippable: true;
        /** Called when "Skip for now" (or the dialog's close control) is used — distinct from `onSubmit`, which always carries a groupId. */
        onSkip: () => void;
      }
    | { skippable?: false; onSkip?: undefined }
  );

/**
 * Group picker shown between row/CSV entry and submission. Shared by the email and CSV
 * invite flows, which pass their own (different) submission logic in via `onSubmit`.
 *
 * `skippable` renders the org-admin "optional" mode (ENTADMIN-1960): the invite has
 * already been sent by this point (see EmailInviteModal), so "no selection" is a valid,
 * intentional outcome (Skip) rather than an error — unlike the mandatory mode, there's
 * no inline validation state for it.
 */
const GroupAssignmentStep = ({
  open,
  onBack,
  onSubmit,
  submitting,
  errorMessage,
  selectedContractId,
  skippable,
  onSkip,
}: GroupAssignmentStepProps) => {
  const manageableGroups = useManageableGroups();
  const { eligibleGroupIds } = useContractEligibleGroupIds(
    React.useMemo(() => manageableGroups.map((group) => group.id), [manageableGroups]),
    selectedContractId
  );
  // Undefined means "don't filter" (single-contract org, or eligibility still loading) —
  // briefly showing the unfiltered list while loading is preferable to a false "No groups
  // found", and IncompatibleContractGroupModal already catches a stale pick either way.
  const groups = eligibleGroupIds
    ? manageableGroups.filter((group) => eligibleGroupIds.has(group.id))
    : manageableGroups;
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | undefined>(undefined);
  const [selectionError, setSelectionError] = React.useState('');

  const handleSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
    if (selectionError) setSelectionError('');
  };

  const handleSubmit = async () => {
    if (!selectedGroupId) {
      if (skippable) return;
      setSelectionError(_t('Choosing a group is mandatory to invite learners.'));
      return;
    }
    try {
      await onSubmit(selectedGroupId);
    } catch {
      // Callers surface their own submission errors via `errorMessage` — this only guards
      // against an uncaught rejection reaching the console.
    }
  };

  return (
    <Dialog open={open} onClose={skippable ? onSkip : onBack} width="medium">
      <HeadingGroup
        supportText={
          skippable
            ? _t('Invites are on their way. You can assign them to a group now, or do it later from the groups page.')
            : _t('Choose the group to add your learners to.')
        }
      >
        {_t('Add learners to a group')}
      </HeadingGroup>
      <Content css={styles.content}>
        {/* Always-present live region so screen readers announce errors when they appear. */}
        <div role="alert">
          {(selectionError || errorMessage) && (
            <InlineNotification severity="error">
              <Typography2 component="p">{selectionError || errorMessage}</Typography2>
            </InlineNotification>
          )}
        </div>
        <GroupSelectList
          groups={groups}
          selectedId={selectedGroupId}
          onSelect={handleSelect}
          ariaLabel={_t('Select a group')}
          optional={skippable}
        />
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: _t('Add to group'),
            loading: submitting,
            disabled: submitting || (skippable && !selectedGroupId),
            onClick: handleSubmit,
          }}
          alternateButtonProps={{
            variant: 'secondary',
            children: skippable ? _t('Skip for now') : _t('Back'),
            disabled: submitting,
            onClick: skippable ? onSkip : onBack,
          }}
        />
      </Actions>
    </Dialog>
  );
};

export default GroupAssignmentStep;
